import type {
    ElementRef,
    ElementType,
    FunctionComponent,
    MutableRefObject,
    ReactElement,
    ReactNode,
} from 'react';
import { createElement, useCallback, useReducer } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box, styled } from '@mui/material';
import {
    createSmoothTransitionStyles,
    isClipPathInsetSupported,
} from './utils';
import { collapsibleBarClasses } from './collapsibleBarClasses';
import type { CollapsedBarMode } from './collapsibleBarModeReducer';
import { modeReducer } from './collapsibleBarModeReducer';
import type {
    CollapsibleBarsContextValue,
    CollapsibleBarSide,
} from './CollapsibleBarsContext.ts';
import {
    CollapsibleBarsContext,
    useCollapsibleBarsContext,
} from './CollapsibleBarsContext.ts';
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// CollapsibleBar
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

const CollapsibleBarRoot = styled(Box, {
    name: 'CollapsibleBar',
    slot: 'root',
    skipVariantsResolver: true,
})({
    //width: 0,
    //zIndex: 10,
    ...createSmoothTransitionStyles('width'),

    display: 'flex',

    // Show the container of the button
    [`&:hover > span.${collapsibleBarClasses.triggerButtonContainer}`]: {
        opacity: 1,
        zIndex: 1,
    },
});

const CollapsibleBarInnerCollapsible = styled(Box, {
    name: 'CollapsibleBar',
    slot: 'innerCollapsible',
    skipVariantsResolver: true,
    shouldForwardProp: (propName) =>
        !['mode', 'side', 'width', 'collapsedSize'].includes(
            propName as string,
        ),
})<{
    mode: CollapsedBarMode;
    side?: CollapsibleBarSide;
    width?: number;
    collapsedSize?: number;
}>(({ mode, width, collapsedSize }) => ({
    height: '100%',
    ...createSmoothTransitionStyles('width'),
    width: mode === 'collapse' ? collapsedSize : width,
    // marginLeft:
    //     side === 'right'
    //         ? // Show the sidebar
    //           mode === 'collapse'
    //             ? -collapsedSize
    //             : -width
    //         : undefined,
}));

const CollapsibleBarContent = styled(Box, {
    name: 'CollapsibleBar',
    slot: 'content',
    skipVariantsResolver: true,
    shouldForwardProp: (propName) =>
        !['sticky', 'stickyTop', 'isClipPathInsetSupported', 'side'].includes(
            propName as string,
        ),
})<{
    sticky: boolean;
    stickyTop?: number | string;
    isClipPathInsetSupported?: boolean;
    side?: CollapsibleBarSide;
}>(({ sticky, stickyTop, isClipPathInsetSupported, side }) => ({
    position: sticky ? 'sticky' : 'static',
    top: sticky ? stickyTop : 'auto',
    height: sticky ? 'auto' : 'inherit',
    // If clip-path is not supported, we fallback to overflow: hidden. In that case it will not be possible
    // for children to use position sticky unless the height is absolute specified
    // If the position of the sidebar is on the right, we must use overflow hidden or the collapsed content
    // will overflow the width of the page
    clipPath: 'inset(0)',
    overflow:
        isClipPathInsetSupported && side !== 'right' ? 'visible' : 'hidden',
}));

const CollapsibleBarInnerContent = styled(Box, {
    name: 'CollapsibleBar',
    slot: 'innerContent',
    skipVariantsResolver: true,
    shouldForwardProp: (propName) =>
        !['sticky', 'width'].includes(propName as string),
})<{
    sticky: boolean;
    width?: number | string;
}>(({ sticky, width }) => ({
    width: width, // If you override this with width: 'auto' you will shrink the content rather than hide it
    height: sticky ? 'auto' : 'inherit',
}));

// These styles are influenced by jira-like Sidebar. Because the `CollapsibleBar` is
// influenced by Jira, we make this position as default because of this requirements.
const CollapsibleBarTriggerButtonContainer = styled('span', {
    name: 'CollapsibleBar',
    slot: 'triggerButtonContainer',
    skipVariantsResolver: true,
    shouldForwardProp: (propName) =>
        !['mode', 'side', 'width', 'collapsedSize', 'sx'].includes(
            propName as string,
        ),
})<{
    mode: CollapsedBarMode;
    side?: CollapsibleBarSide;
    width?: number;
    collapsedSize?: number;
}>(({ mode, side, width, collapsedSize }) => ({
    ...createSmoothTransitionStyles('opacity', 'transform'),
    opacity: mode === 'collapse' ? 1 : 0,
    width: 0,
    order: -1,
    zIndex: mode !== 'visible' ? 1 : undefined,
    //zIndex: 20,
    // NOTE: We use `transform` instead of `left` and `right`,
    // because `transform` doesn't trigger reflow in some browsers.
    // Check - https://csstriggers.com/transform.

    ...// There must be a better way so we dont need an additional transition by placing the
    // button thingy elsewhere in the dom
    (side === 'left'
        ? {
              background: 'red',
              transform: `translateX(${
                  mode === 'collapse' ? collapsedSize : width
              }px)`,
          }
        : {
              //transform: `translateX(-${mode === 'collapse' ? collapsedSize : width}px)`
          }),
    '&:hover': {
        opacity: 1,
    },
    '& > *': {
        position: 'sticky',
        top: '50%',
        // todo 13 is based on that we have a button that is 26px. We can do better
        marginLeft: '-13px',
        marginTop: '-13px',
    },
}));

type CommonProps = {
    className?: string;
    //    classes?: { [key: string]: string };
    children?: ReactNode;
};

export type TriggerButtonProps = {
    mode: CollapsedBarMode;
};

export type BaseCollapsibleBarProps = CommonProps & {
    /**
     * The element which will be used as the collapsible bar. Default to `div`.
     * */
    as?: ElementType;
    sx?: SxProps<Theme>;
    side: CollapsibleBarSide;
    // /**
    //  * Width of the collapsed sidebar. Default to `0`.
    //  * */
    // collapsedSize?: number;
    // /**
    //  * Width of the bar which is not yet collapsed.
    //  * */
    // width?: number;
    triggerButtonComponent?: ReactElement<TriggerButtonProps>;
    /**
     * A render prop which will change the default button use to trigger the collapse behaviour.
     * */
    renderButton?: (props: {
        mode: CollapsedBarMode;
        onClick(): void;
        /**
         * This `className` handles the computed position of the `triggerButton`.
         * The position is influenced by the Jira Sidebar. If we don't want
         * this behaviour, don't use this `className`.
         * */
        positionClassName: string;
    }) => JSX.Element;
    /**
     *  TODO:
     * - We don't need to declare `renderButton` and `hideDefaultButton` props for the toggle button. We can simplify this via
     *   declaring single prop that manage the toggle button. E.g if user don't pass a renderButton then dont show the pre-defined button.
     */
    hideDefaultButton?: boolean;
    /**
     * A render prop which returns the `CollapsibleBar` elements. If define, this would take higher
     * precedence than the `children` element.
     * */
    render?: (props: { mode: CollapsedBarMode }) => ReactNode;
    sidebarRef?: MutableRefObject<ElementRef<'aside'>>;
    /**
     * Should the content be sticky to the top
     */
    sticky?: boolean;
    /**
     * We use either render({mode}) or children for the content of the sidebar
     */
    children?: ReactNode;
};

const useCollapsibleBar = ({
    sidebarRef,
    as = 'aside',
    sx,
    side,
    // this render prop is accepting a Component. We need to make sure
    // that we gonna call this as a Component through `<RenderButton />`.
    // Without doing this, or call it like `renderButton(props)`, if the `renderButton` is using hooks then React will treat as a custom hooks instead
    // of Component. Because of this, calling this `renderButton` inside
    // conditional expression will possibly lead to bugs.
    renderButton: RenderButton,
    hideDefaultButton = false,
    render: renderChildren,
    children,
    className,
    sticky = false,
}: BaseCollapsibleBarProps) => {
    const ctx = useCollapsibleBarsContext();
    const config = ctx[side];

    const sidebarWidth = config.width;
    const sidebarCollapsedWidth = config.collapsedWidth;
    const { mode, modeTransition, disabled } = config;

    const childrenEl = renderChildren ? renderChildren({ mode }) : children;

    const handleClick = useCallback(
        () => modeTransition('CLICK'),
        [modeTransition],
    );

    // If the `CollapsibleBars` is disabled, just return immediately
    // the children.
    if (disabled) {
        return createElement(as, { className }, childrenEl);
    }

    const handleMouseEnter = () => modeTransition('MOUSE_ENTER');
    const handleMouseLeave = () => modeTransition('MOUSE_LEAVE');

    /**
     * These values are used for testing. A little hack in here for testing.
     * This is very helpful if we are testing 2 `CollapsibleBar` at the same time where every `CollapsibleBar` has its own `TriggerButton`.
     * */
    const collapsibleBarTestID = `test-collapsible-bar-${side}`;
    // This `data-testid` will only be added in default button.
    // If we going to use this `CollapsibleBar`, we basically provide our
    // own trigger button. Note that the positioning of the `button` is fixed.
    // Means we can't change the positioning of the button because it is wrapped
    // to a `span` element which provides the positioning.
    const buttonTestID = `trigger-button-${side}`;

    const renderButtonProps = {
        mode,
        onClick: handleClick,
        positionClassName: collapsibleBarClasses.triggerButton,
    };
    const button =
        !hideDefaultButton &&
        (RenderButton ? (
            <RenderButton {...renderButtonProps} />
        ) : (
            <button
                data-testid={buttonTestID}
                onClick={handleClick}
                className={collapsibleBarClasses.triggerButton}
            />
        ));

    return (
        <>
            <CollapsibleBarRoot
                component={as}
                sx={sx}
                width={
                    (mode === 'expand' ? sidebarWidth : sidebarCollapsedWidth) +
                    'px'
                }
                zIndex={mode === 'visible' ? 10 : undefined}
                order={side === 'right' ? 1 : undefined}
                justifyContent={side === 'left' ? 'flex-start' : 'flex-end'}
                className={[className, collapsibleBarClasses.root]
                    .filter(Boolean)
                    .join(' ')}
                ref={sidebarRef}
                data-testid={collapsibleBarTestID}
            >
                <CollapsibleBarTriggerButtonContainer
                    mode={mode}
                    side={side}
                    width={sidebarWidth}
                    collapsedSize={sidebarCollapsedWidth}
                    onMouseEnter={() => {
                        // This is a little bit hack. Basically what we want is to retain the `VISIBLE` state when moving to the trigger button.
                        // Without this, moving to the trigger button will `collapse` the `CollapsibleBar`.
                        if (mode === 'visible') {
                            modeTransition('MOUSE_ENTER');
                        }
                    }}
                    onMouseLeave={handleMouseLeave}
                    className={collapsibleBarClasses.triggerButtonContainer}
                >
                    {button}
                </CollapsibleBarTriggerButtonContainer>

                <CollapsibleBarInnerCollapsible
                    mode={mode}
                    side={side}
                    width={sidebarWidth}
                    collapsedSize={sidebarCollapsedWidth}
                    className={collapsibleBarClasses.innerCollapsible}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <CollapsibleBarContent
                        sticky={sticky}
                        stickyTop={0}
                        isClipPathInsetSupported={isClipPathInsetSupported}
                        side={side}
                        className={collapsibleBarClasses.content}
                    >
                        <CollapsibleBarInnerContent
                            sticky={sticky}
                            width={sidebarWidth}
                            className={collapsibleBarClasses.innerContent}
                        >
                            {childrenEl}
                        </CollapsibleBarInnerContent>
                    </CollapsibleBarContent>
                </CollapsibleBarInnerCollapsible>
            </CollapsibleBarRoot>
        </>
    );
};

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// Composed CollapsibleBar
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

type CollapsibleBarProps = Omit<BaseCollapsibleBarProps, 'side'>;

export const CollapsibleBarLeft: FunctionComponent<CollapsibleBarProps> = (
    props,
) => {
    return useCollapsibleBar({
        side: 'left',
        ...props,
    });
};

export const CollapsibleBarRight: FunctionComponent<CollapsibleBarProps> = (
    props,
) => {
    return useCollapsibleBar({
        side: 'right',
        ...props,
    });
};

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// CollapsibleBarContent
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

export const CollapsibleBarsContent = styled(Box)({
    // Solves issue with tables pushing the content width over 100%, which
    // also prevents overflow: auto to function
    // See: https://stackoverflow.com/questions/36230944/prevent-flex-items-from-overflowing-a-container/66689926#66689926
    // """
    //   In other words, because display:flex; results in min-width:auto;, then the element cannot shrink below it's
    //   requested width, thus it will never create an overflow within itself
    // """
    minWidth: 0,

    flexGrow: 1,

    //zIndex: 1,
});

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// CollapsibleBars
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

export type CollapsibleBarsProps = CommonProps & {
    sx?: SxProps<Theme>;
    sidebars?: Partial<
        Record<
            CollapsibleBarSide,
            {
                disabled?: boolean;
                width: number;
                collapsedWidth: number;
                initiallyCollapsed?: boolean;
                element: ReactElement;
            }
        >
    >;

    contentAs?: ElementType;
};

const CollapsibleBarsRoot = styled(Box)({
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexGrow: 1,
});

const useSidebarSideContextConfig = (
    side: CollapsibleBarSide,
    sidebars?: CollapsibleBarsProps['sidebars'],
): CollapsibleBarsContextValue[CollapsibleBarSide] => {
    const config = sidebars[side];
    const initMode = config?.initiallyCollapsed ? 'collapse' : 'expand';
    const [mode, modeTransition] = useReducer(modeReducer, initMode);

    return {
        disabled: config?.disabled ?? false,
        width: config?.width ?? 0,
        collapsedWidth: config?.collapsedWidth ?? 0,
        mode,
        modeTransition,
    };
};
export const CollapsibleBars: FunctionComponent<CollapsibleBarsProps> = ({
    className,
    sx,
    children,
    sidebars,
    contentAs = 'div',
}) => {
    // space between content and left/right bar used for expand/collapse.
    const value: CollapsibleBarsContextValue = {
        left: useSidebarSideContextConfig('left', sidebars),
        right: useSidebarSideContextConfig('right', sidebars),
    };
    return (
        <CollapsibleBarsContext.Provider value={value}>
            <CollapsibleBarsRoot
                data-testid={`test-collapsible-bars`}
                className={className}
                sx={sx}
            >
                {!value.left.disabled && sidebars?.left?.element}
                {!value.right.disabled && sidebars?.right?.element}
                <CollapsibleBarsContent as={contentAs}>
                    {children}
                </CollapsibleBarsContent>
            </CollapsibleBarsRoot>
        </CollapsibleBarsContext.Provider>
    );
};
